import { AdditionalActivatedChallengeFields, CreateActivatedChallengeResponse, GetPublicOrganizationProductResponse, LoginOrigin, PaymentTargetType, TranslationPhrases } from '../types';
import { GoogleTagEvent, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { addActivatedChallenge, createActivateChallengeRequest, getActivatedChallengesForAccount, resendChallengeActivationEmail } from '../helpers/api/challenge-api';
import challengeData, { challengeHasEnded, challengeNotLive, challengeNotStarted, challengeTypeName } from '../helpers/state/challenge-data';
import { customElement, property, query, state } from 'lit/decorators';
import { detectMobileViewport, inIframe, isInAppWebview, storageBlocked } from '../helpers/device';
import { getFromLocalStorage, getFromSessionStorage, isDefined, isValidString, removeSessionStorage, setSessionStorage, show_confetti, undefinedIfEmpty } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { FormCheckbox } from '../components/theme/form-checkbox';
import { PreCheckoutComponent } from '../components/pre-checkout-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { getMainPage } from '../helpers/page-name';
import { getOrganizationProductsPublic } from '../helpers/api/products-api';
import { getQueryStringParameters } from '../helpers/urls';
import pointCache from '../helpers/state/point-cache';
import { startStripeCheckoutSession } from '../helpers/api/stripe-api';
import { storeReferralFromUrl } from '../helpers/api/referral-api';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';
import { normalizePhoneNumber, validateEmail } from '../helpers/strings';
import { suggestEmailCorrection } from '../helpers/email-typos';
import { LOGIN_LOOKUP_ERROR_TOAST, NO_REGISTRATION_FOUND_NOTICE, REGISTRATION_NOTICE_KEY, REGISTRATION_TOAST_KEY } from '../helpers/constants';

@customElement('registration-page')
export class RegistrationPage extends LitElement {

    amplitude_callsite: string = 'Registration Page';

    @query('#username')
    usernameElement!: HTMLInputElement;

    @query('#email')
    emailElement!: HTMLInputElement;

    @query('#phoneNumber')
    phoneNumberElement!: HTMLInputElement;

    @query('#optIn')
    optInElement!: FormCheckbox;

    @query('#phoneOptIn')
    phoneOptInElement!: FormCheckbox;

    @query('#resendEmail')
    resendEmailElement!: HTMLInputElement;

    @query('#resendPhoneNumber')
    resendPhoneNumberElement!: HTMLInputElement;

    @query('#code')
    codeElement!: HTMLInputElement;

    @query('#preCheckout')
    private readonly preCheckoutElement?: PreCheckoutComponent | null;

    @property()
    challenge_id: string ='';

    @property()
    isInMap: boolean = false;

    get challenge() {
        return challengeData.challenge;
    }

    @property({ type: Boolean })
    is_embedded: boolean = false;

    @state()
    emailSuggestion?: string;

    @state()
    isRegistered: boolean = false;

    @state()
    resendEmailForm: boolean = false;

    @state()
    pendingLoginEmail?: string;

    @state()
    showLogin: boolean = false;

    @state()
    notice?: string;

    @state()
    storageTipDismissed: boolean = false;

    get showStorageTip() {
        return !this.storageTipDismissed && (isInAppWebview() || storageBlocked());
    }

    // True when the visitor arrived by scanning a check-in QR code (the redirect carries its key).
    get fromQrScan(): boolean {
        return isDefined(getQueryStringParameters().get('challenge_location_qr_code_key'));
    }

    @state()
    registrationProducts: GetPublicOrganizationProductResponse[] = [];

    @state()
    private translatedLabels: TranslationPhrases = {};

    @state()
    isRegistering: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindChallengeState = new StateController(this, challengeData);

    private _lastKnownTotal: string = '';
    private _totalCheckInterval: number | undefined;

    // [mhaughey] Keep up with total cost changes from pre-checkout component
    // Using polling for now since I'm having trouble getting events to work across components.
    // Could revisit later with a different approach if needed.
    connectedCallback() {
        super.connectedCallback();

        // Poll for changes every 500ms
        this._totalCheckInterval = setInterval(() => {
            if (isDefined(this.preCheckoutElement)) {
                const currentTotal = this.preCheckoutElement?.totalCostString;
                if (currentTotal !== this._lastKnownTotal) {
                    this._lastKnownTotal = currentTotal;
                    this.requestUpdate();
                }
            }
        }, 500);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (isDefined(this._totalCheckInterval)) {
            clearInterval(this._totalCheckInterval);
        }
    }

    async firstUpdated() {
        // Set default lang attribute for screen readers
        if (!document.documentElement.lang) {
            document.documentElement.lang = 'en';
        }
        translationData.onLanguageChanged(() => {
            void this.translateLabels();
        });
        await this.startup();
    }

    // Labels rendered through attributes (textfield labels, placeholders) can't use
    // <translated-span>, so they go through the imperative translateFields pattern instead.
    private get labelPhrases(): TranslationPhrases {
        const phrases: TranslationPhrases = {
            name: 'Name',
            emailAddress: 'Email Address',
            phoneNumber: 'Phone Number',
            phoneNumberWithCountry: 'Phone Number (Including Country Code)',
            code: 'Code',
            dismiss: 'Dismiss',
        };
        this.challenge?.registration_settings?.additional_fields?.forEach((field, index) => {
            phrases[`field_${index}`] = field.label;
            field.options?.forEach((option, optionIndex) => {
                phrases[`field_${index}_option_${optionIndex}`] = option;
            });
        });
        return phrases;
    }

    private async translateLabels() {
        this.translatedLabels = await translationData.translateFields(this.labelPhrases);
    }

    async startup() {
        // get challenge ID and activated challenge id from the query parameters
        const queryString = getQueryStringParameters();
        const topicId = queryString.get('topic_id');
        const topicKey = queryString.get('topic_key');
        const challengeId = queryString.get('challenge_id');
        const href = window.location.href;
        this.isRegistering = false;
        try {
            await storeReferralFromUrl(href);
        } catch (error) {
            console.error('Failed to store referral from URL:', error);
        }

        if (!topicData.isReady || !this.is_embedded) {
            await topicData.instantiateTopicData(
                this.amplitude_callsite,
                accountInfo.isAuthenticated,
                true,
                true,
                topicId,
                topicKey
            );

            if (!isDefined(topicData.topic) || !isDefined(topicData.challengeId)) {
                getMainPage().errorToastElement.showToastAndThrow('Could not load challenge data. Please try again later.');
                return;
            }

            if (challengeData.challenge?._id?.$oid !== challengeId && isDefined(challengeId)) {
                await challengeData.instantiateChallengeData(
                    challengeId ?? topicData.challengeId,
                    topicData.topicKey,
                    topicData.activatedChallengeId,
                    topicData.activatedChallengeKey,
                    false
                );
            }

            // check if the user is already registered for the challenge
            if (!isDefined(challengeData.challenge)) {
                getMainPage().errorToastElement.showToastAndThrow('Could not load challenge data. Please try again later.');
                return;
            }
        }
        const localStorageRegistered = isDefined(localStorage.getItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}`));
        this.isRegistered = localStorageRegistered ? true : isDefined(challengeData.activated_challenge) ?? false;

        // Surface a one-shot notice (e.g. login didn't match a registration, or the registration was
        // removed) once the data is loaded, then clear it so it doesn't reappear on later visits.
        const pendingNotice = getFromSessionStorage(REGISTRATION_NOTICE_KEY);
        if (isDefined(pendingNotice)) {
            this.notice = pendingNotice;
            removeSessionStorage(REGISTRATION_NOTICE_KEY);
        }

        const pendingToast = getFromSessionStorage(REGISTRATION_TOAST_KEY);
        if (isDefined(pendingToast)) {
            getMainPage().errorToastElement.showToast(pendingToast);
            removeSessionStorage(REGISTRATION_TOAST_KEY);
        }

        // No point cache on this page — registration only needs the topic's languages.
        await translationData.instantiateTranslations(true, false, true);
        await this.translateLabels();

        await this.getRegistrationProducts();
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        // Track registration page view
        logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.REGISTRATION_PAGE_VIEW);
    }

    async resendEmail() {
        if (!this.resendEmailElement?.value && !this.resendPhoneNumberElement?.value) {
            getMainPage().errorToastElement.showToast('Please enter your email address or phone number');
            return;
        }

        try {
            await resendChallengeActivationEmail(this.challenge?._id.$oid ?? '', this.resendEmailElement?.value, this.resendPhoneNumberElement?.value);
            getMainPage().errorToastElement.showToast('Email has been resent');
            this.resendEmailForm = false;
        } catch (error) {
            getMainPage().errorToastElement.showToast('Could not resend the email. The email address or phone number may be incorrect.');
        }
    }

    newRegistration() {
        this.isRegistered = false;

        localStorage.removeItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}`);
        localStorage.removeItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_id`);
        localStorage.removeItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_write_key`);

        this.requestUpdate();
    }

    async sendLoginCode() {
        if (!this.emailElement?.value) {
            getMainPage().errorToastElement.showToast('Please enter your email address');
            return;
        }

        const email = this.emailElement?.value;

        try {
            await login(this.amplitude_callsite, email, LoginOrigin.Challenge, challengeData.challenge?.name, topicData.topic?.header_logo);
            this.pendingLoginEmail = email;
            this.requestUpdate();
        } catch (error) {
            getMainPage().errorToastElement.showToast('Could not send the login code. Please try again later.');
        }
    }

    async verifyLoginCode() {
        const code = this.codeElement?.value;
        if (!this.pendingLoginEmail) {
            getMainPage().errorToastElement.showToast('Please enter your email address to receive a code');
            return;
        }

        try {
            await upgradeToken(
                this.amplitude_callsite,
                this.pendingLoginEmail,
                code,
                getFromLocalStorage('organization_id') ?? undefined,
                LoginOrigin.Challenge,
            );
        } catch (error) {
            getMainPage().errorToastElement.showToast('Could not verify the code. Please try again later.');
            return;
        }

        // Now signed in — send them straight to their participation page for this challenge.
        let participationLink: string | undefined;
        try {
            participationLink = await this.resolveParticipationLinkForEmail(this.pendingLoginEmail);
        } catch (error) {
            // A lookup failure (network/server) is NOT the same as "no registration" — don't show the
            // misleading notice. Surface a plain-language toast (on reload) so the user can report it.
            console.error('Failed to resolve participation link after login', error);
            setSessionStorage(REGISTRATION_TOAST_KEY, LOGIN_LOOKUP_ERROR_TOAST);
            location.reload();
            return;
        }

        if (participationLink) {
            window.location.href = participationLink;
            return;
        }

        // Signed in but no registration found for this challenge with that email — send them back to
        // the registration form with a notice (people often mistype or mix up which email they used).
        setSessionStorage(REGISTRATION_NOTICE_KEY, NO_REGISTRATION_FOUND_NOTICE);
        location.reload();
    }

    private get hasTranslationLanguages() {
        return (topicData.topic?.translation_languages?.length ?? 0) > 0;
    }

    private get hasCoverHeader() {
        return !this.isInMap && isValidString(this.challenge?.registration_settings?.cover_image_url);
    }

    renderLanguageSelectorRow() {
        return this.hasTranslationLanguages && !this.hasCoverHeader ? html`
            <div class="language-selector-row"><language-selector></language-selector></div>
        ` : '';
    }

    renderHeaderLanguageSelector() {
        return this.hasTranslationLanguages && this.hasCoverHeader ? html`
            <div class="language-selector-corner"><language-selector></language-selector></div>
        ` : '';
    }

    renderStorageTip() {
        if (!this.showStorageTip) {
            return '';
        }
        const message = isInAppWebview()
            ? 'Opened from another app? Tap ••• and choose “Open in browser” so you stay signed in.'
            : 'Your browser isn’t saving your sign-in — add this to your home screen to stay signed in.';
        return html`
            <div class="registration-notice" role="status">
                <span><translated-span>${message}</translated-span></span>
                <button type="button" class="registration-notice__dismiss" aria-label="${this.translatedLabels.dismiss ?? 'Dismiss'}" @click="${() => { this.storageTipDismissed = true; }}">×</button>
            </div>
        `;
    }

    renderAlreadyRegisteredCta() {
        return html`
            <div class="already-registered-cta">
                <span class="already-registered-cta__label"><translated-span>You scanned a check-in code, but you’re not logged in yet. If you’ve already registered, log in to check in.</translated-span></span>
                <form-button
                    variant="outline"
                    fullWidth
                    @click="${() => { this.showLogin = true; }}">
                    <translated-span>Log in to check in</translated-span>
                </form-button>
                <button type="button" class="link-button" @click="${() => { this.resendEmailForm = true; }}"><translated-span>Or resend your secret link</translated-span></button>
            </div>
        `;
    }

    async resolveParticipationLinkForEmail(email: string): Promise<string | undefined> {
        const challengeId = this.challenge?._id.$oid;
        if (!challengeId) {
            return undefined;
        }
        // Match the canonical (trimmed + lowercased) form: accounts and registrations are stored
        // lowercased, and the account-lookup endpoint authorizes on an exact email match.
        const normalizedEmail = email.trim().toLowerCase();
        const accountChallenges = await getActivatedChallengesForAccount(this.amplitude_callsite, normalizedEmail);
        const match = accountChallenges?.find((c) => c.challenge_id.$oid === challengeId);
        const link = match?.link_set?.activated_challenge_page_write_link;
        return link ? topicData.swapUrlForOrgUrl(link) : undefined;
    }

    get isFullyLoaded() {
        return topicData.isReady &&
               challengeData.isReady &&
               isDefined(challengeData.challenge);
    }

    render() {
        if (!this.isFullyLoaded) {
            return this.renderLoading();
        }
        if (this.resendEmailForm) {
            return this.remderReSendEmailForm();
        }
        if (this.showLogin) {
            return this.renderLogIn();
        }
        if (this.isRegistered) {
            return this.renderAlreadyRegistered();
        }
        if (this.is_embedded) {
            return this.renderInner();
        }
        return html`
            <div class="wrapper ${challengeData.challenge?.is_registration_active ?? false ? '' : 'wrapper_small'} ${detectMobileViewport() || this.is_embedded ? 'wrapper_mobile' : ''}">
                ${this.renderInner()}
            </div>
        `;
    }

    renderLoading() {
        return html`
        <loading-element
            .loadingString=${'Loading Registration Page...'}
            ></loading-element>
        `;
    }

    renderInner() {
        return html`
            <div class="card ${inIframe() ? 'card-iframe' : ''} ${detectMobileViewport() || this.is_embedded ? 'card_mobile' : ''}">
                ${this.isInMap ? '' : html`
                    <div class="card-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                        ${this.renderHeaderLanguageSelector()}
                        ${challengeData.challenge?.registration_settings?.cover_image_url ? html`
                            <div class="card-image-holder card-cover-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                                <img src="${this.challenge?.registration_settings?.cover_image_url ?? ''}" alt="Challenge Cover Image">

                                ${this.challenge?.registration_settings?.logo_url ? html`
                                    <div class="card-logo-overlay">
                                        <img src="${this.challenge?.registration_settings?.logo_url ?? ''}" alt="Challenge Logo" style="${challengeData.challenge?.registration_settings?.show_logo_background ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                                    </div>
                                ` : ''}
                            </div>
                        ` : this.challenge?.registration_settings?.logo_url ? html`
                            <div class="card-image-holder card-logo-only ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                                <img src="${this.challenge?.registration_settings?.logo_url ?? ''}" alt="Challenge Logo" style="${challengeData.challenge?.registration_settings?.show_logo_background === true ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                            </div>
                        ` : ''}
                    </div>
                `}
                <div class="card-content ${detectMobileViewport() || this.is_embedded ? 'card-content_mobile' : ''} ${this.isInMap ? 'card-content-in-map' : ''}">
                    <div class="card-content__scrollable">
                        ${this.renderLanguageSelectorRow()}
                        ${this.notice ? html`
                            <div class="registration-notice" role="status">
                                <span><translated-span>${this.notice}</translated-span></span>
                                <button type="button" class="registration-notice__dismiss" aria-label="${this.translatedLabels.dismiss ?? 'Dismiss'}" @click="${() => { this.notice = undefined; }}">×</button>
                            </div>
                        ` : ''}
                        ${this.renderStorageTip()}
                        ${this.renderChallengeDetails()}
                        ${this.fromQrScan ? this.renderAlreadyRegisteredCta() : ''}
                        <br>
                        ${challengeData.challenge?.is_registration_active ?? false ? this.renderForm() : this.renderRegistrationClosed()}
                    </div>
                    ${challengeData.challenge?.is_registration_active ?? false ? html`
                        <div class="card-content__sticky-footer">
                            <form-button
                                size="lg"
                                fullWidth
                                ?disabled="${this.isRegistering}"
                                aria-busy="${this.isRegistering}"
                                @click="${this.register}">
                                ${this.submitFormText}
                            </form-button>
                            ${this.fromQrScan ? '' : html`
                                <div class="already-registered already-registered--prominent">
                                    <form-button
                                        size="lg"
                                        fullWidth
                                        variant="outline"
                                        @click="${() => {this.showLogin = true;}}">
                                        <translated-span>Already Registered? Log In</translated-span>
                                    </form-button>
                                </div>
                            `}
                        </div>
                    ` : ''}
                </div>
            </div>

        `;
    }

    checkEmailForTypo() {
        this.emailSuggestion = suggestEmailCorrection(this.emailElement?.value);
    }

    acceptEmailSuggestion() {
        if (isDefined(this.emailSuggestion) && isDefined(this.emailElement)) {
            this.emailElement.value = this.emailSuggestion;
        }
        this.emailSuggestion = undefined;
    }

    async register() {
        if (this.isRegistering) {
            return;
        }
        this.isRegistering = true;

        if (!this.emailElement?.value) {
            this.isRegistering = false;
            getMainPage().errorToastElement.showToast('Please enter your email address');
            return;
        }

        // A half-typed address (`lucia@`) used to register fine and then silently
        // never receive the participation link.
        if (!validateEmail(this.emailElement.value.trim())) {
            this.isRegistering = false;
            getMainPage().errorToastElement.showToast("That email address doesn't look complete — please check it and try again");
            return;
        }

        if (this.emailElement?.value !== accountInfo.account?.email?.toLowerCase() && isDefined(undefinedIfEmpty(accountInfo.account?.email))) {
            accountInfo.clearAccountData();
        }

        if (this.challenge?.terms && (this.challenge?.registration_settings?.opt_in_required ?? true) && !(this.optInElement.checked ?? false)) {
            getMainPage().errorToastElement.showToast('Please agree to the terms and conditions to register');
            return;
        }
        let phone_opted_out = false;
        if (this.challenge?.registration_settings?.show_phone === true) {
            phone_opted_out = !(this.phoneOptInElement?.checked ?? true);
        }

        // Best-effort: normalize to E.164 when we can, but never block
        // registration on phone format — keep whatever they typed otherwise.
        const phoneEntry = this.phoneNumberElement?.value?.trim();
        const phoneValue: string | undefined = normalizePhoneNumber(phoneEntry);
        if (!isDefined(phoneValue) && this.challenge?.registration_settings?.phone_required === true) {
            // Empty but required — presence check, not a format check.
            this.isRegistering = false;
            getMainPage().errorToastElement.showToastAndThrow('Phone number is required');
            return;
        }

        if ((this.challenge?.registration_settings?.username_required ?? false) && (this.usernameElement?.value === '' || this.usernameElement?.value === undefined)) {
            this.isRegistering = false;
            getMainPage().errorToastElement.showToastAndThrow('Please enter your name or team name');
            return;
        }

        const additionalFields: AdditionalActivatedChallengeFields[] = [];

        if (this.challenge?.registration_settings?.additional_fields) {
            this.challenge.registration_settings.additional_fields.forEach((field, index) => {
                const value = field.field_type === 'checkbox' ?
                    (this.shadowRoot?.querySelector(`#${`field-${index}`}`) as FormCheckbox)?.checked === true ? 'true' : 'false' :
                    (this.shadowRoot?.querySelector(`#${`field-${index}`}`) as HTMLInputElement)?.value;
                if (field.required && (!isDefined(value) || value === '')) {
                    this.isRegistering = false;
                    getMainPage().errorToastElement.showToastAndThrow(`Please enter a value for ${field.label}`);
                    return;
                }
                additionalFields.push({
                    field_name: field.label,
                    field_value: value ?? '',
                });
            });
        }
                // later add check for writer, already paid, edit etc.
        const paymentRequired = this.visibleProductIds.length > 0 && !topicData.isWriter && isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0;

        if (paymentRequired && isDefined(this.preCheckoutElement)) {
            const email = this.emailElement?.value ?? '';
            const request = createActivateChallengeRequest(email, this.challenge?._id.$oid ?? '', phoneValue, this.usernameElement?.value ?? 'Participant', phone_opted_out, additionalFields);

            try {
                const response = await startStripeCheckoutSession(this.emailElement?.value ?? '', this.preCheckoutElement.remainingLineItems, topicData.organizationId ?? '', challengeData.challenge?._id.$oid ?? '', PaymentTargetType.ActivatedChallenge, request, undefined, topicData.topicKey, undefined);
                // open url in same tab
                if (isDefined(response) && isDefined(response.url)) {
                    window.location.href = response.url;
                }
                this.isRegistering = false;
            } catch (error) {
                this.isRegistering = false;
                getMainPage().errorToastElement.showToast('Could not start the checkout process. You may already be registered. Please contact support.');
            }
        } else {
            try {
                const added_challenge = await addActivatedChallenge(this.amplitude_callsite, this.emailElement?.value, this.challenge?._id.$oid ?? '', phoneValue, this.usernameElement?.value ?? 'Participant', phone_opted_out, additionalFields, topicData.topicKey);
                if (isDefined(added_challenge._id)) {
                    getMainPage().errorToastElement.showToast('You have been registered for the challenge!');

                    localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}`, 'true');
                    localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_id`, added_challenge._id.$oid);
                    localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_write_key`, added_challenge.ac_write_key);

                    if (this.emailElement?.value) {
                        void show_confetti([topicData.backgroundColor, topicData.textColor]);
                    }

                    await this.startup();
                    this.requestUpdate();
                } else {
                    this.isRegistering = false;
                    getMainPage().errorToastElement.showToast('Could not register for the challenge. Please try again later.');
                }
            } catch (error) {
                this.isRegistering = false;
                getMainPage().errorToastElement.showToast('Could not register for the challenge. You may already be registered. Try "Resend your secret link" or "Log in" to access your challenge.');
            }
        }
    }


    async processCheckoutSuccess(added_challenge: CreateActivatedChallengeResponse) {
         if (isDefined(added_challenge._id)) {
                getMainPage().errorToastElement.showToast('You have been registered for the challenge!');

                localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}`, 'true');
                localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_id`, added_challenge._id.$oid);
                localStorage.setItem(`activated_challenge_${challengeData.challenge?._id.$oid ?? ''}_write_key`, added_challenge.ac_write_key);

                if (this.emailElement?.value) {
                    void show_confetti([topicData.backgroundColor, topicData.textColor]);
                }

                // [ARoney] "Lifecycle" methods should not be called like this.  We should refactor this to use a "startup" function that `firstUpdated` calls.
                await this.startup();
                this.requestUpdate();
            } else {
                getMainPage().errorToastElement.showToast('Could not register for the challenge. Please try again later.');
        }
    }

    renderChallengeDetails() {
        return html`
            <h1><translated-span>${this.challenge?.registration_settings?.heading}</translated-span></h1>
            <p><translated-span>${this.challenge?.registration_settings?.description}</translated-span></p>
        `;
    }

    goToChallengePage() {
        let link = challengeData.activated_challenge?.link_set?.activated_challenge_page_write_link;
        link = topicData.swapUrlForOrgUrl(link ?? '');
        if (link) {
            window.open(link, '_blank');
        }
    }

    renderAlreadyRegistered() {
        return html`
            <div class="${this.is_embedded ? '' : 'wrapper wrapper_small'}">
                <div class="card ${inIframe() ? 'card-iframe' : ''} ${detectMobileViewport() || this.is_embedded ? 'card_mobile' : ''}">
                    ${this.isInMap ? '' : html`
                        <div class="card-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                        ${this.renderHeaderLanguageSelector()}
                            ${challengeData.challenge?.registration_settings?.cover_image_url ? html`
                                <div class="card-image-holder card-cover-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                                    <img src="${this.challenge?.registration_settings?.cover_image_url ?? ''}" alt="Challenge Cover Image">

                                    ${this.challenge?.registration_settings?.logo_url ? html`
                                        <div class="card-logo-overlay">
                                            <img src="${this.challenge?.registration_settings?.logo_url ?? ''}" alt="Challenge Logo" style="${challengeData.challenge?.registration_settings?.show_logo_background ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                                        </div>
                                    ` : ''}
                                </div>
                            ` : this.challenge?.registration_settings?.logo_url ? html`
                                <div class="card-image-holder card-logo-only ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                                    <img src="${this.challenge?.registration_settings?.logo_url ?? ''}" alt="Challenge Logo" style="${challengeData.challenge?.registration_settings?.show_logo_background === true ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                                </div>
                            ` : ''}
                        </div>
                    `}
                    <div class="card-content ${detectMobileViewport() || this.is_embedded ? 'card-content_mobile' : ''}">
                        ${this.renderLanguageSelectorRow()}
                        <h1><translated-span>You are registered for</translated-span> ${this.challenge?.name}</h1>
                        ${challengeData.activated_challenge?.link_set?.activated_challenge_page_write_link ? html`
                            <form-button
                                size="lg"
                                style="margin-top: 1rem;cursor: pointer;"
                                fullWidth
                                @click="${this.goToChallengePage}">
                                <translated-span>Go to Your Unique Participation Page</translated-span>
                            </form-button>
                        ` : html`
                            <p><translated-span>Check your email for your secret participation link!</translated-span> ${this.challenge?.is_live === true ? '' : html`<translated-span>You will be notified when the challenge begins!</translated-span>`}</p>
                            <p class="resend"><translated-span>Need your secret link again?</translated-span> <button class="link-button" @click="${() => {this.resendEmailForm = true;}}"><translated-span>Resend it</translated-span></button> <translated-span>or</translated-span> <button class="link-button" @click="${() => {this.showLogin = true;}}"><translated-span>log in here by confirming your email</translated-span></button></p>
                            <p><translated-span>Need to register another participant?</translated-span> <button class="link-button" @click="${this.newRegistration}"><translated-span>Click here.</translated-span></button></p>
                        `}
                    </div>
                </div>
            </div>
        `;
    }

    renderRegistrationClosed() {
        return html`
            ${challengeHasEnded(this.challenge, challengeData.activated_challenge?.is_tester) ? html`
                    <h3><translated-span>Registration Closed</translated-span></h3>
                    <p><translated-span>This ${challengeTypeName(this.challenge)} has ended and is no longer accepting new registrations.</translated-span></p>
            ` : challengeNotStarted(this.challenge, challengeData.activated_challenge?.is_tester) ? html`
                    <h3><translated-span>Registration Closed</translated-span></h3>
                    <p><translated-span>Registration for this ${challengeTypeName(this.challenge)} will open soon. Stay tuned!</translated-span></p>
            ` : challengeNotLive(this.challenge) ? html`
                    <h3><translated-span>Registration Closed</translated-span></h3>
                    <p><translated-span>Registration for this ${challengeTypeName(this.challenge)} is currently closed.</translated-span></p>
            ` : ''}
        `;
    }

    remderReSendEmailForm() {
        return html`
            <div class="${this.is_embedded ? '' : 'wrapper wrapper_small'}">
                <div class="card ${inIframe() ? 'card-iframe' : ''} ${detectMobileViewport() || this.is_embedded ? 'card_mobile' : ''}">
                    <div class="card-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                        ${this.renderHeaderLanguageSelector()}
                        <img src="${this.challenge?.registration_settings?.cover_image_url ?? ''}" alt="Challenge Image">
                    </div>
                    <div class="card-content ${detectMobileViewport() || this.is_embedded ? 'card-content_mobile' : ''}">
                        ${this.renderLanguageSelectorRow()}
                        <p><translated-span>Enter your email and/or phone number to receive your secret participation link</translated-span></p>
                        <proxi-textfield
                            id="resendEmail"
                            outlined
                            value="${undefinedIfEmpty(accountInfo.account?.email) ?? getFromLocalStorage('email') ?? ''}"
                            label="${this.translatedLabels.emailAddress ?? 'Email Address'}">
                        </proxi-textfield>
                        <proxi-textfield
                            id="resendPhoneNumber"
                            outlined
                            helperText="+1 (555) 555-5555"
                            value="${accountInfo.account?.phone_number ?? '+1 '}"
                            label="${this.translatedLabels.phoneNumberWithCountry ?? 'Phone Number (Including Country Code)'}">
                        </proxi-textfield>
                        <form-button
                            size="lg"
                            style="margin-top: 1rem;cursor: pointer;"
                            fullWidth
                            @click="${this.resendEmail}">
                            <translated-span>Resend Email</translated-span>
                        </form-button>
                        <p class="auth-alt"><translated-span>Prefer to log in with a code?</translated-span> <button class="link-button" @click="${() => {this.resendEmailForm = false; this.showLogin = true;}}"><translated-span>Log in by confirming your email</translated-span></button></p>
                        <button class="link-button auth-back" @click="${() => {this.resendEmailForm = false;}}"><translated-span>Back to registration</translated-span></button>
                    </div>
                </div>
            </div>
        `;
    }

    renderLogIn() {
        return html`
            <div class="${this.is_embedded ? '' : 'wrapper wrapper_small'}">
                <div class="card ${inIframe() ? 'card-iframe' : ''} ${detectMobileViewport() || this.is_embedded ? 'card_mobile' : ''}">
                    ${this.isInMap ? '' : html`
                        <div class="card-image ${detectMobileViewport() && !this.is_embedded ? 'card-image_mobile_full_page' : detectMobileViewport() || this.is_embedded ? 'card-image_mobile' : ''}">
                        ${this.renderHeaderLanguageSelector()}
                            <img src="${this.challenge?.registration_settings?.cover_image_url ?? ''}" alt="Challenge Image">
                        </div>
                    `}
                    <div class="card-content ${detectMobileViewport() || this.is_embedded ? 'card-content_mobile' : ''}">
                        ${this.renderLanguageSelectorRow()}
                        <h1><translated-span>Log in by verifying email.</translated-span></h1>
                        ${this.renderStorageTip()}
                        ${this.pendingLoginEmail ? html`
                            <p><translated-span>Enter the code you received in your email</translated-span> (${this.pendingLoginEmail}).</p>
                            <button class="link-button" @click="${() => {this.showLogin = false; this.pendingLoginEmail = undefined;}}"><translated-span>Back</translated-span></button>
                            <proxi-textfield
                                id="code"
                                outlined
                                label="${this.translatedLabels.code ?? 'Code'}">
                            </proxi-textfield>
                            <form-button
                                size="lg"
                                style="margin-top: 1rem;cursor: pointer;"
                                fullWidth
                                @click="${this.verifyLoginCode}">
                                <translated-span>Go To Your Page</translated-span>
                            </form-button>
                            <p class="email-help"><translated-span>Didn't get the email? It can take a minute to arrive — and be sure to check your spam or junk folder.</translated-span></p>

                            ` : html`
                            <p><translated-span>Enter your email address and we'll send you a code to enter below.</translated-span></p>
                            <proxi-textfield
                                id="email"
                                outlined
                                value="${getFromLocalStorage('email') ?? ''}"
                                label="${this.translatedLabels.emailAddress ?? 'Email Address'}">
                            </proxi-textfield>
                            <form-button
                                size="lg"
                                style="margin-top: 1rem;cursor: pointer;"
                                fullWidth
                                @click="${this.sendLoginCode}">
                                <translated-span>Send Code</translated-span>
                            </form-button>
                        `}
                        <p class="auth-alt"><translated-span>Prefer a link instead?</translated-span> <button class="link-button" @click="${() => {this.showLogin = false; this.pendingLoginEmail = undefined; this.resendEmailForm = true;}}"><translated-span>Resend your secret link</translated-span></button></p>
                        <button class="link-button auth-back" @click="${() => {this.showLogin = false; this.pendingLoginEmail = undefined;}}"><translated-span>Back to registration</translated-span></button>
                    </div>
                </div>
            </div>
        `;
    }

    renderForm() {
        return html`
            <proxi-textfield
                id="username"
                outlined
                ?required="${this.challenge?.registration_settings?.username_required}"
                value="${accountInfo.account?.full_name ?? ''}"
                label="${this.translatedLabels.name ?? 'Name'}">
            </proxi-textfield>
            <proxi-textfield
                id="email"
                type="email"
                outlined
                ?required="${true}"
                value="${accountInfo.account?.email ?? ''}"
                @blur="${this.checkEmailForTypo}"
                @change="${this.checkEmailForTypo}"
                label="${this.translatedLabels.emailAddress ?? 'Email Address'}">
            </proxi-textfield>
            ${isDefined(this.emailSuggestion) ? html`
                <p class="email-help"><translated-span>Did you mean</translated-span>
                    <button class="link-button" @click="${this.acceptEmailSuggestion}">${this.emailSuggestion}</button>?</p>
            ` : html`
                <p class="email-help"><translated-span>We'll send your link here, so give it a quick double-check.</translated-span></p>
            `}
            ${this.challenge?.registration_settings?.show_phone ?? true ? html`
                <proxi-textfield
                    id="phoneNumber"
                    type="tel"
                    outlined
                    ?required="${this.challenge?.registration_settings?.phone_required}"
                    value="${accountInfo.account?.phone_number ?? ''}"
                    label="${this.translatedLabels.phoneNumber ?? 'Phone Number'}"
                    placeholder="e.g. +61 412 345 678">
                </proxi-textfield>
            ` : ''}
            ${this.challenge?.registration_settings?.additional_fields?.map((field,index) => {
                switch (field.field_type) {
                    case 'text':
                        return html`
                            <div class="extra-field">
                                <proxi-textfield
                                    id="${`field-${index}`}"
                                    outlined
                                    label="${this.translatedLabels[`field_${index}`] ?? field.label}"
                                    ?required="${field.required}"
                                    >
                                </proxi-textfield>
                            </div>
                        `;
                    case 'number':
                        return html`
                            <div class="extra-field">
                                <proxi-textfield
                                    id="${`field-${index}`}"
                                    outlined
                                    label="${this.translatedLabels[`field_${index}`] ?? field.label}"
                                    ?required="${field.required}"
                                    type="number"
                                    >
                                </proxi-textfield>
                            </div>
                        `;
                    case 'checkbox':
                        return html`
                            <div class="extra-field">
                                <form-checkbox
                                    id="${`field-${index}`}"
                                    name="${field.label}${field.required ? '*' : ''}"
                                    class_name="blue"
                                    ?required="${field.required}">
                                    ${this.translatedLabels[`field_${index}`] ?? field.label}
                                </form-checkbox>
                            </div>
                        `;
                    case 'select':
                        return html`
                            <div class="extra-field">
                                <proxi-selectfield
                                    id="${`field-${index}`}"
                                    outlined
                                    label="${this.translatedLabels[`field_${index}`] ?? field.label}"
                                    ?required="${field.required}"
                                    >
                                    ${field.options?.map((option, optionIndex) => html`
                                        <mwc-list-item value="${option}">${this.translatedLabels[`field_${index}_option_${optionIndex}`] ?? option}</mwc-list-item>
                                    `)}
                                </proxi-selectfield>
                            </div>
                        `;
                    default:
                        return html``;
                }
            })}

            ${this.challenge?.terms ? html`
                <div class="checkbox-wrapper">
                    <form-checkbox
                        id="optIn"
                        name="requireEmail"
                        class_name="blue"
                        ><translated-span>I agree to the</translated-span> <a href="${this.challenge?.terms}" target="_blank" rel="noopener noreferrer"><translated-span>terms and conditions</translated-span></a>
                    </form-checkbox>
                </div>
            ` : ''}

            ${this.challenge?.registration_settings?.show_phone === true ? html`
                <div class="checkbox-wrapper">
                    <form-checkbox
                        id="optInPhone"
                        name="optInPhone"
                        class_name="blue"
                        > <translated-span>Text me updates about this challenge.</translated-span></form-checkbox>
                </div>
                <div class="opt-in-text">
                    <p>
                        <translated-span>Automated marketing texts, frequency varies, msg & data rates may apply. Not required to take part. Reply STOP to quit, HELP for help. See our</translated-span> <a href="https://www.proxi.co/d/terms-of-service" target="_blank"><translated-span>Terms of Service</translated-span></a> <translated-span>and</translated-span> <a href="https://www.proxi.co/d/privacy-policy" target="_blank"><translated-span>Privacy Policy</translated-span></a>.
                    </p>
                </div>
            ` : ''}
            ${this.registrationProducts.length === 0 ? '' : html`
                <pre-checkout-component
                    id="preCheckout"
                    .product_ids=${this.visibleProductIds}
                    .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                    .payment_target_type=${PaymentTargetType.ActivatedChallenge}
                >
                </pre-checkout-component>
            `}
        `;
    }

    /// Payment Helpers
    async getRegistrationProducts() {
        this.registrationProducts = await getOrganizationProductsPublic(PaymentTargetType.ActivatedChallenge, challengeData.challenge?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '');
    }

    get submitFormText() {
        if (this.isRegistering) {
            return html`<translated-span>Registering...</translated-span>`;
        }

        const hasPayment = isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0;
        return html`<span><translated-span>${undefinedIfEmpty(this.challenge?.registration_settings?.button_label) ?? 'Register'}</translated-span>${hasPayment ? html` <translated-span>and Pay</translated-span> ${this.preCheckoutElement?.totalCostString}` : ''}</span>`;
    }

    get visibleProductIds(): string[] {
        const visibleProductIds: string[] = [];
        for (const product of this.registrationProducts) {
            if (product.status !== 'active') {
                continue;
            }
            visibleProductIds.push(product._id.$oid);
        }
        return visibleProductIds;
    }

    static get styles() {
        return css`
            .language-selector-row {
                display: flex;
                justify-content: flex-end;
            }

            .language-selector-corner {
                position: absolute;
                top: 0.5rem;
                right: 1rem;
                z-index: 2;
            }

            .registration-notice {
                display: flex;
                align-items: flex-start;
                gap: 0.5rem;
                padding: 0.75rem 1rem;
                margin-bottom: 1rem;
                border-radius: 8px;
                background-color: #fff4e5;
                border: 1px solid #ffd8a8;
                color: #663c00;
                font-size: 0.9rem;
                line-height: 1.35;
            }

            .registration-notice__dismiss {
                margin-left: auto;
                background: none;
                border: none;
                color: inherit;
                font-size: 1.1rem;
                line-height: 1;
                cursor: pointer;
                padding: 0;
            }

            .already-registered-cta {
                display: flex;
                flex-direction: column;
                align-items: stretch;
                gap: 0.5rem;
                padding: 0.85rem 1rem;
                margin: 0.5rem 0 1rem;
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-radius: 10px;
                background-color: rgba(0, 0, 0, 0.03);
                text-align: center;
            }

            .already-registered-cta__label {
                font-weight: 600;
                font-size: 0.95rem;
            }

            .email-help {
                margin-top: 0.75rem;
                font-size: 0.85rem;
                opacity: 0.7;
            }

            .auth-alt {
                margin-top: 1.25rem;
                font-size: 0.9rem;
                text-align: center;
            }

            .auth-back {
                display: block;
                margin: 0.5rem auto 0;
                font-size: 0.85rem;
                opacity: 0.7;
            }

            /* When embedded inside the check-in drawer, fill the parent flex
               container so card_mobile's height:100% and the sticky-footer
               pattern in card-content__scrollable work as designed. Without
               this, the host element resolves to display:inline with no
               definite height, so children can't lay out vertically. */
            :host([is_embedded]) {
                display: flex;
                flex-direction: column;
                flex: 1 1 auto;
                min-height: 0;
                width: 100%;
            }

            .wrapper {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                min-height: 300px;
                gap: 10px;
                background-color: var(--theme-border-color);
            }

            .wrapper_small {
                min-height: 200px;
                background-color: transparent;
            }

            .wrapper_mobile {
                align-items: flex-start;
                height: auto;
            }

            h1 {
                font-size: 1.2rem;
                margin-bottom: 5px;
                margin-top: 5px;
            }

            .link {
                cursor: pointer;
                text-decoration: underline;
            }

            .link-button {
                background: none;
                border: none;
                padding: 0;
                margin: 0;
                color: inherit;
                font: inherit;
                font-size: inherit;
                cursor: pointer;
                text-decoration: underline;
            }

            .link-button:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
                border-radius: 2px;
            }

            .card {
                display: flex;
                flex-direction: row;
                align-items: stretch;
                width: 50%;
                border: 1px solid #ccc;
                border-radius: 10px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                background-color: white;
                box-sizing: border-box;
                max-height: 90vh;
            }

            .card-iframe {
                width: calc(100vw - 20px);
                height: calc(100vh - 20px);
            }

            .card-image {
                position: relative;
                width: 50%;
                align-self: stretch;
                overflow: hidden;

                display: flex;
                align-items: center;
                justify-content: center;
                border-bottom-right-radius: 10px;
                border-top-right-radius: 10px;
                padding-right: 10px;
            }

            .card-image-holder {
                position: relative; /* Make it a positioning context */
                overflow: hidden;
                height: 100%;
                width: 100%;
                display: flex;
                border-bottom-left-radius: 10px;
                border-top-left-radius: 10px;
            }

            .card-cover-image img {
                overflow: hidden;
                object-fit: cover;
                width: 100%;
                height: 100%;
            }

            .card-logo-overlay {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                justify-content: center;
                align-items: center;
                max-width: 80%;
                max-height: 80%;
                z-index: 2;
            }

            .card-logo-overlay img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                background-color: white;
                border-radius: 6px;
                padding: 5px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            }

            .card-logo-only {
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .card-logo-only img {
                max-width: 80%;
                max-height: 80%;
                object-fit: contain;
                background-color: white;
                border-radius: 10px;
                padding: 10px;
            }

            .card-content-in-map {
                text-align: center;
            }

            .card-content {
                width: 50%;
                display: flex;
                flex-direction: column;
                padding: 1.5rem;
                /* No overflow here — .card-content__scrollable below is the
                   single scroll container, with the sticky footer pinned
                   beneath it. A second overflow on this parent produced
                   the nested double-scroll users could see on desktop. */
                min-height: 0;
                gap: 4px;
            }

            .checkbox-wrapper {
                display: flex;
                align-items: center;
            }


            .card_mobile {
                flex-direction: column;
                width: 100%;
                height: 100%;
                border: none;
                box-shadow: none;
                max-height: 100vh;
                max-height: 100dvh;
                overflow: hidden;
            }

            .card-image_mobile_full_page {
                width: 100%;
                height: 100%;
                border-bottom-right-radius: 0;
                border-top-right-radius: 10px;
                border-top-left-radius: 10px;
                border-bottom-left-radius: 0;
                max-height: 100vh;
            }

            .card-image_mobile {
                width: 100%;
                height: 100px;
                border-bottom-right-radius: 0;
                border-top-right-radius: 10px;
                border-top-left-radius: 10px;
                border-bottom-left-radius: 0;
            }

            .card-image_mobile_full_page {
                width: 100%;
                max-height: 30vh;
                border-bottom-right-radius: 0;
                border-top-right-radius: 10px;
                border-top-left-radius: 10px;
                border-bottom-left-radius: 0;
            }

            .extra-field {
                display: flex;
                flex-direction: column;
                gap: 4px;
                margin-bottom: 5px;
            }
            .extra-field span {
                font-size: 0.8rem;
                color: var(--theme-text-color);
                font-weight: 500;
                margin-bottom: 4px;
            }

            .card-content_mobile {
                width: unset;
                padding: 0.75rem 1rem;
                flex: 1 1 auto;
                min-height: 0;
            }

            .card-content_mobile h1 {
                font-size: 1.15rem;
                margin: 0 0 0.5rem;
                line-height: 1.25;
            }

            .card-content_mobile .card-content__scrollable > p {
                font-size: 0.85rem;
                line-height: 1.35;
                margin: 0 0 0.75rem;
                color: var(--theme-text-color);
                opacity: 0.85;
            }

            .card-content__scrollable {
                flex: 1;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding-right: 0.5rem;
            }

            .card-content_mobile .card-content__scrollable {
                gap: 2px;
                padding-right: 0.25rem;
            }

                        /* width */
            .card-content__scrollable::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .card-content__scrollable::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .card-content__scrollable::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }
            .card-content__sticky-footer {
                padding-top: 0.75rem;
                border-top: 1px solid #eee;
                flex-shrink: 0;
            }

            .card-content_mobile .card-content__sticky-footer {
                padding-top: 0.5rem;
            }

            .card-content_mobile .card-content__sticky-footer p {
                font-size: 0.85rem;
                line-height: 1.3;
                margin: 0.5rem 0 0;
            }

            .already-registered--prominent {
                text-align: center;
                margin-top: 0.75rem;
            }

            .card-content__sticky-footer .already-registered--prominent .already-registered__heading {
                font-size: 1.15rem;
                font-weight: 600;
                margin: 0 0 0.6rem;
            }

            .already-registered--prominent form-button {
                display: block;
            }

            .card-content__sticky-footer .already-registered--prominent .already-registered__subtext {
                font-size: 0.9rem;
                margin: 0.6rem 0 0;
                color: #555;
            }
        `;
    }
}
